import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, ArrowLeft, Activity, Wind, Scale, AlertTriangle, CheckCircle, Loader, ChevronRight, Calendar } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useAuth, authHeaders } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const months = ['Ocak','Subat','Mart','Nisan','Mayis','Haziran','Temmuz','Agustos','Eylul','Ekim','Kasim','Aralik'];
const days = ['Pazar','Pazartesi','Sali','Carsamba','Persembe','Cuma','Cumartesi'];

const formatTodayLong = () => {
  const d = new Date();
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${days[d.getDay()]}`;
};

const formatTodayISO = () => new Date().toISOString().slice(0, 10);

// ══════════════════════════════════════
// BLOOD PRESSURE TAB
// ══════════════════════════════════════
const BPTab = ({ onSaved }) => {
  const [session, setSession] = useState('morning');
  const [m1, setM1] = useState({ systolic: '', diastolic: '', pulse: '' });
  const [m2, setM2] = useState({ systolic: '', diastolic: '', pulse: '' });
  const [device, setDevice] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/api/cardio/blood-pressure/history?days=7`, { headers: authHeaders() });
      if (res.ok) setHistory(await res.json());
    } catch {}
  };

  const submit = async () => {
    if (!m1.systolic || !m1.diastolic) return;
    setSaving(true);
    try {
      const body = {
        session_type: session,
        measurement_1: { systolic: +m1.systolic, diastolic: +m1.diastolic, pulse: m1.pulse ? +m1.pulse : null },
        ...(m2.systolic && m2.diastolic ? { measurement_2: { systolic: +m2.systolic, diastolic: +m2.diastolic, pulse: m2.pulse ? +m2.pulse : null } } : {}),
        device_type: device,
      };
      const res = await fetch(`${API}/api/cardio/blood-pressure`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (res.ok) { setResult(await res.json()); onSaved?.(); loadHistory(); }
    } catch {} finally { setSaving(false); }
  };

  const colorMap = { emerald: 'text-emerald-400', amber: 'text-amber-400', orange: 'text-orange-400', red: 'text-red-400', blue: 'text-blue-400' };

  if (result) {
    const c = result.classification || {};
    return (
      <div className="space-y-4" data-testid="bp-result">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
          <p className="text-3xl font-bold text-white">{result.systolic}/{result.diastolic}</p>
          <p className="text-white/40 text-xs mt-1">mmHg</p>
          <span className={`${colorMap[c.color] || 'text-white/60'} text-sm font-medium mt-2 inline-block`}>{c.label}</span>
        </div>
        {result.red_flags?.length > 0 && result.red_flags.map((f, i) => (
          <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-red-300 text-xs">{f.message}</p>
          </div>
        ))}
        {result.device_warning && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
            <p className="text-amber-200 text-xs">{result.device_warning}</p>
          </div>
        )}
        <button onClick={() => setResult(null)} className="w-full py-2.5 bg-white/10 text-white/70 rounded-xl text-sm hover:bg-white/15 transition" data-testid="bp-new-reading-btn">
          Yeni Ölçüm Ekle
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bp-form">
      {/* Today indicator */}
      <div className="flex items-center gap-2 text-white/40 text-xs">
        <Calendar className="w-3.5 h-3.5 text-rose-400" />
        <span>{formatTodayLong()} kaydi</span>
      </div>

      {/* Session type */}
      <div className="flex gap-2">
        {[['morning', 'Sabah'], ['evening', 'Aksam']].map(([val, label]) => (
          <button key={val} onClick={() => setSession(val)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${session === val ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30' : 'bg-white/5 text-white/50 border border-white/10'}`}
            data-testid={`bp-session-${val}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Measurement 1 */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <p className="text-white/60 text-xs font-medium mb-3">1. Ölçüm</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-white/40 text-[10px]">Sistolik</label>
            <input type="number" value={m1.systolic} onChange={e => setM1({ ...m1, systolic: e.target.value })}
              placeholder="120" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center" data-testid="bp-m1-systolic" />
          </div>
          <div>
            <label className="text-white/40 text-[10px]">Diyastolik</label>
            <input type="number" value={m1.diastolic} onChange={e => setM1({ ...m1, diastolic: e.target.value })}
              placeholder="80" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center" data-testid="bp-m1-diastolic" />
          </div>
          <div>
            <label className="text-white/40 text-[10px]">Nabiz</label>
            <input type="number" value={m1.pulse} onChange={e => setM1({ ...m1, pulse: e.target.value })}
              placeholder="72" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center" data-testid="bp-m1-pulse" />
          </div>
        </div>
      </div>

      {/* Measurement 2 */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <p className="text-white/60 text-xs font-medium mb-3">2. Ölçüm (Önerilen)</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-white/40 text-[10px]">Sistolik</label>
            <input type="number" value={m2.systolic} onChange={e => setM2({ ...m2, systolic: e.target.value })}
              placeholder="118" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center" data-testid="bp-m2-systolic" />
          </div>
          <div>
            <label className="text-white/40 text-[10px]">Diyastolik</label>
            <input type="number" value={m2.diastolic} onChange={e => setM2({ ...m2, diastolic: e.target.value })}
              placeholder="78" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center" data-testid="bp-m2-diastolic" />
          </div>
          <div>
            <label className="text-white/40 text-[10px]">Nabiz</label>
            <input type="number" value={m2.pulse} onChange={e => setM2({ ...m2, pulse: e.target.value })}
              placeholder="70" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm text-center" data-testid="bp-m2-pulse" />
          </div>
        </div>
      </div>

      {/* Device */}
      <div>
        <label className="text-white/40 text-xs">Cihaz Tipi (Opsiyonel)</label>
        <input type="text" value={device} onChange={e => setDevice(e.target.value)}
          placeholder="Omron M3, Withings BPM..." className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm mt-1" data-testid="bp-device" />
      </div>

      <button onClick={submit} disabled={!m1.systolic || !m1.diastolic || saving}
        className={`w-full py-3 rounded-xl font-semibold text-sm transition ${m1.systolic && m1.diastolic && !saving ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
        data-testid="bp-submit-btn">
        {saving ? <Loader className="w-4 h-4 animate-spin mx-auto" /> : 'Kaydet'}
      </button>

      {/* Recent History + Trend Chart */}
      {history && history.readings?.length > 0 && (
        <div className="space-y-4 mt-4">
          {/* Trend Chart */}
          {history.readings.length >= 2 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="bp-trend-chart">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/60 text-xs font-medium">Tansiyon Trendi</p>
                {history.trend && history.trend.trend !== 'no_data' && history.trend.trend !== 'no_recent' && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${history.trend.trend === 'rising' ? 'bg-red-500/15 text-red-300' : history.trend.trend === 'falling' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-blue-500/15 text-blue-300'}`}>
                    {history.trend.label}
                  </span>
                )}
              </div>
              <div className="h-[160px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history.readings.slice(0, 20).reverse().map(r => {
                    const d = r.timestamp?.slice(5, 10)?.replace('-', '/');
                    return { date: d, s: r.avg_systolic, d: r.avg_diastolic, session: r.session_type === 'morning' ? 'S' : 'A' };
                  })} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} axisLine={false} tickLine={false} />
                    <YAxis domain={['auto', 'auto']} tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 11 }}
                      labelStyle={{ color: 'rgba(255,255,255,0.5)' }}
                      formatter={(val, name) => [val + ' mmHg', name === 's' ? 'Sistolik' : 'Diyastolik']}
                    />
                    <ReferenceLine y={140} stroke="rgba(239,68,68,0.3)" strokeDasharray="3 3" />
                    <ReferenceLine y={90} stroke="rgba(239,68,68,0.15)" strokeDasharray="3 3" />
                    <ReferenceLine y={120} stroke="rgba(34,211,238,0.2)" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="s" stroke="#FB7185" strokeWidth={2} dot={{ fill: '#FB7185', r: 3 }} name="s" />
                    <Line type="monotone" dataKey="d" stroke="#38BDF8" strokeWidth={2} dot={{ fill: '#38BDF8', r: 3 }} name="d" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 mt-2">
                <span className="flex items-center gap-1 text-[10px] text-white/40"><span className="w-2 h-2 rounded-full bg-rose-400 inline-block" /> Sistolik</span>
                <span className="flex items-center gap-1 text-[10px] text-white/40"><span className="w-2 h-2 rounded-full bg-sky-400 inline-block" /> Diyastolik</span>
              </div>
              {/* Average */}
              {history.trend?.avg_systolic > 0 && (
                <div className="flex items-center justify-center gap-3 mt-2">
                  <span className="text-white/30 text-[10px]">Ortalama:</span>
                  <span className="text-white/60 text-xs font-medium">{history.trend.avg_systolic}/{history.trend.avg_diastolic} mmHg</span>
                </div>
              )}
            </div>
          )}

          {/* History List */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="bp-history">
            <p className="text-white/60 text-xs font-medium mb-3">Son Olcumler</p>
            <div className="space-y-2">
              {history.readings.slice(0, 7).map((r, i) => {
                const dateStr = r.timestamp?.slice(0, 10);
                const isToday = dateStr === formatTodayISO();
                return (
                  <div key={i} className={`flex items-center justify-between py-1.5 ${isToday ? 'bg-rose-500/5 -mx-2 px-2 rounded-lg' : ''}`}>
                    <span className={`text-[10px] ${isToday ? 'text-rose-300 font-medium' : 'text-white/40'}`}>
                      {isToday ? 'Bugün' : dateStr} {r.session_type === 'morning' ? 'S' : 'A'}
                    </span>
                    <span className="text-white text-xs font-medium">{r.avg_systolic}/{r.avg_diastolic}</span>
                    <span className={`text-[10px] ${colorMap[r.classification?.color] || 'text-white/50'}`}>{r.classification?.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════
// RHYTHM TAB
// ══════════════════════════════════════
const RhythmTab = ({ onSaved }) => {
  const [form, setForm] = useState({ palpitation: false, irregular_beat: false, dizziness: false, syncope: false, shortness_of_breath: false, duration_minutes: '', time_of_day: '', trigger: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [redFlags, setRedFlags] = useState([]);

  const toggle = (key) => setForm(p => ({ ...p, [key]: !p[key] }));

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...form, duration_minutes: form.duration_minutes ? +form.duration_minutes : 0 };
      const res = await fetch(`${API}/api/cardio/rhythm`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (res.ok) { const d = await res.json(); setSaved(true); setRedFlags(d.red_flags || []); onSaved?.(); }
    } catch {} finally { setSaving(false); }
  };

  if (saved) return (
    <div className="space-y-4" data-testid="rhythm-result">
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
        <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
        <p className="text-emerald-300 text-sm">Kaydedildi</p>
      </div>
      {redFlags.map((f, i) => (
        <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs">{f.message}</p>
        </div>
      ))}
      <button onClick={() => { setSaved(false); setForm({ palpitation: false, irregular_beat: false, dizziness: false, syncope: false, shortness_of_breath: false, duration_minutes: '', time_of_day: '', trigger: '' }); }}
        className="w-full py-2.5 bg-white/10 text-white/70 rounded-xl text-sm">Yeni Kayit</button>
    </div>
  );

  const symptoms = [
    ['palpitation', 'Çarpıntı oldu'],
    ['irregular_beat', 'Düzensiz atım hissi'],
    ['dizziness', 'Bas donmesi / bayilma hissi'],
    ['syncope', 'Bayilma'],
    ['shortness_of_breath', 'Nefes darlığı eşlik etti'],
  ];

  return (
    <div className="space-y-4" data-testid="rhythm-form">
      <div className="flex items-center gap-2 text-white/40 text-xs">
        <Calendar className="w-3.5 h-3.5 text-rose-400" />
        <span>{formatTodayLong()} kaydi</span>
      </div>
      <p className="text-white/50 text-xs">Son yaşadığınız ritim/çarpıntı olayını kaydedin.</p>
      {symptoms.map(([key, label]) => (
        <button key={key} onClick={() => toggle(key)}
          className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-sm transition ${form[key] ? 'bg-rose-500/15 border border-rose-500/30 text-rose-300' : 'bg-white/5 border border-white/10 text-white/60'}`}
          data-testid={`rhythm-${key}`}>
          <span>{label}</span>
          <span className="text-xs">{form[key] ? 'Evet' : 'Hayir'}</span>
        </button>
      ))}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-white/40 text-[10px]">Sure (dakika)</label>
          <input type="number" value={form.duration_minutes} onChange={e => setForm(p => ({ ...p, duration_minutes: e.target.value }))}
            placeholder="5" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" data-testid="rhythm-duration" />
        </div>
        <div>
          <label className="text-white/40 text-[10px]">Zaman</label>
          <input type="text" value={form.time_of_day} onChange={e => setForm(p => ({ ...p, time_of_day: e.target.value }))}
            placeholder="Sabah, aksam..." className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" data-testid="rhythm-time" />
        </div>
      </div>
      <div>
        <label className="text-white/40 text-[10px]">Tetikleyici (opsiyonel)</label>
        <input type="text" value={form.trigger} onChange={e => setForm(p => ({ ...p, trigger: e.target.value }))}
          placeholder="Kafein, stres, egzersiz..." className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" data-testid="rhythm-trigger" />
      </div>
      <button onClick={submit} disabled={saving}
        className="w-full py-3 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-xl text-sm transition" data-testid="rhythm-submit-btn">
        {saving ? <Loader className="w-4 h-4 animate-spin mx-auto" /> : 'Kaydet'}
      </button>
    </div>
  );
};

// ══════════════════════════════════════
// CHEST PAIN TAB
// ══════════════════════════════════════
const ChestPainTab = ({ onSaved }) => {
  const [form, setForm] = useState({ pressure_tightness: false, effort_related: false, resolves_with_rest: true, radiation: false, radiation_areas: '', shortness_of_breath: false, fatigue: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [redFlags, setRedFlags] = useState([]);

  const toggle = (key) => setForm(p => ({ ...p, [key]: !p[key] }));

  const submit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cardio/chest-pain`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      if (res.ok) { const d = await res.json(); setSaved(true); setRedFlags(d.red_flags || []); onSaved?.(); }
    } catch {} finally { setSaving(false); }
  };

  if (saved) return (
    <div className="space-y-4" data-testid="chest-result">
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
        <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
        <p className="text-emerald-300 text-sm">Kaydedildi</p>
      </div>
      {redFlags.map((f, i) => (
        <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs">{f.message}</p>
        </div>
      ))}
      <button onClick={() => { setSaved(false); setForm({ pressure_tightness: false, effort_related: false, resolves_with_rest: true, radiation: false, radiation_areas: '', shortness_of_breath: false, fatigue: false }); }}
        className="w-full py-2.5 bg-white/10 text-white/70 rounded-xl text-sm">Yeni Kayit</button>
    </div>
  );

  const items = [
    ['pressure_tightness', 'Göğüste baskı / sıkışma hissi'],
    ['effort_related', 'Eforla geldi'],
    ['resolves_with_rest', 'Dinlenince gecti'],
    ['radiation', 'Yayilim oldu (kol, boyun, cene, sirt)'],
    ['shortness_of_breath', 'Nefes darlığı eşlik etti'],
    ['fatigue', 'Yorgunluk eşlik etti'],
  ];

  return (
    <div className="space-y-4" data-testid="chest-form">
      <div className="flex items-center gap-2 text-white/40 text-xs">
        <Calendar className="w-3.5 h-3.5 text-rose-400" />
        <span>{formatTodayLong()} kaydi</span>
      </div>
      <p className="text-white/50 text-xs">Göğüs rahatsızlığı/efor belirtilerinizi kaydedin.</p>
      {items.map(([key, label]) => (
        <button key={key} onClick={() => toggle(key)}
          className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-sm transition ${form[key] ? 'bg-rose-500/15 border border-rose-500/30 text-rose-300' : 'bg-white/5 border border-white/10 text-white/60'}`}
          data-testid={`chest-${key}`}>
          <span>{label}</span>
          <span className="text-xs">{form[key] ? 'Evet' : 'Hayir'}</span>
        </button>
      ))}
      {form.radiation && (
        <input type="text" value={form.radiation_areas} onChange={e => setForm(p => ({ ...p, radiation_areas: e.target.value }))}
          placeholder="Hangi bolgelere yayildi?" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" data-testid="chest-radiation-areas" />
      )}
      <button onClick={submit} disabled={saving}
        className="w-full py-3 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-xl text-sm transition" data-testid="chest-submit-btn">
        {saving ? <Loader className="w-4 h-4 animate-spin mx-auto" /> : 'Kaydet'}
      </button>
    </div>
  );
};

// ══════════════════════════════════════
// HEART FAILURE TAB
// ══════════════════════════════════════
const HFTab = ({ onSaved }) => {
  const [form, setForm] = useState({ weight_kg: '', ankle_swelling: false, orthopnea: false, pnd: false, dry_cough: false, exercise_intolerance: false, appetite_loss: false, abdominal_bloating: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [redFlags, setRedFlags] = useState([]);

  const toggle = (key) => setForm(p => ({ ...p, [key]: !p[key] }));

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...form, weight_kg: form.weight_kg ? +form.weight_kg : null };
      const res = await fetch(`${API}/api/cardio/heart-failure`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (res.ok) { const d = await res.json(); setSaved(true); setRedFlags(d.red_flags || []); onSaved?.(); }
    } catch {} finally { setSaving(false); }
  };

  if (saved) return (
    <div className="space-y-4" data-testid="hf-result">
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
        <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
        <p className="text-emerald-300 text-sm">Kaydedildi</p>
      </div>
      {redFlags.map((f, i) => (
        <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs">{f.message}</p>
        </div>
      ))}
      <button onClick={() => { setSaved(false); setForm({ weight_kg: '', ankle_swelling: false, orthopnea: false, pnd: false, dry_cough: false, exercise_intolerance: false, appetite_loss: false, abdominal_bloating: false }); }}
        className="w-full py-2.5 bg-white/10 text-white/70 rounded-xl text-sm">Yeni Kayit</button>
    </div>
  );

  const symptoms = [
    ['ankle_swelling', 'Bacak/ayak bileği şişliği'],
    ['orthopnea', 'Düz yatınca nefes darlığı'],
    ['pnd', 'Gece nefes darlığı ile uyanma'],
    ['dry_cough', 'Kuru oksuruk'],
    ['exercise_intolerance', 'Efor kapasitesi dususu'],
    ['appetite_loss', 'İştah azalması'],
    ['abdominal_bloating', 'Karinda sislik hissi'],
  ];

  return (
    <div className="space-y-4" data-testid="hf-form">
      <div className="flex items-center gap-2 text-white/40 text-xs">
        <Calendar className="w-3.5 h-3.5 text-rose-400" />
        <span>{formatTodayLong()} kaydi</span>
      </div>
      <p className="text-white/50 text-xs">Günlük belirtilerinizi kaydedin.</p>
      {/* Weight */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Scale className="w-4 h-4 text-rose-400" />
          <label className="text-white/60 text-xs font-medium">Günlük Kilo (kg)</label>
        </div>
        <input type="number" step="0.1" value={form.weight_kg} onChange={e => setForm(p => ({ ...p, weight_kg: e.target.value }))}
          placeholder="72.5" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" data-testid="hf-weight" />
      </div>
      {symptoms.map(([key, label]) => (
        <button key={key} onClick={() => toggle(key)}
          className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-sm transition ${form[key] ? 'bg-rose-500/15 border border-rose-500/30 text-rose-300' : 'bg-white/5 border border-white/10 text-white/60'}`}
          data-testid={`hf-${key}`}>
          <span>{label}</span>
          <span className="text-xs">{form[key] ? 'Evet' : 'Hayir'}</span>
        </button>
      ))}
      <button onClick={submit} disabled={saving}
        className="w-full py-3 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-xl text-sm transition" data-testid="hf-submit-btn">
        {saving ? <Loader className="w-4 h-4 animate-spin mx-auto" /> : 'Kaydet'}
      </button>
    </div>
  );
};

// ══════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════
const tabs = [
  { id: 'bp', label: 'Tansiyon', icon: Activity },
  { id: 'rhythm', label: 'Ritim', icon: Heart },
  { id: 'chest', label: 'Göğüs', icon: Wind },
  { id: 'hf', label: 'KY Belirtileri', icon: Scale },
];

const CardioTrackingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'bp';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [summary, setSummary] = useState(null);

  useEffect(() => { loadSummary(); }, []);

  const loadSummary = async () => {
    try {
      const res = await fetch(`${API}/api/cardio/summary`, { headers: authHeaders() });
      if (res.ok) setSummary(await res.json());
    } catch {}
  };

  return (
    <div className="min-h-screen bg-transparent py-6 px-4" data-testid="cardio-tracking-page">
      <div className="max-w-lg mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/50 hover:text-white/80 transition">
            <ArrowLeft className="w-4 h-4" /> Geri
          </button>
        </div>

        <div className="text-center">
          <Heart className="w-10 h-10 text-rose-400 mx-auto mb-2" />
          <h1 className="text-xl font-bold text-white">Kalp ve Dolasim Takibi</h1>
          <p className="text-white/50 text-xs mt-1">Tansiyon, ritim, göğüs belirtileri ve kalp yetersizliği takibi</p>
        </div>

        {/* Today's Date Banner */}
        <div className="flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-xl py-2.5 px-4" data-testid="today-date-banner">
          <Calendar className="w-4 h-4 text-rose-400" />
          <span className="text-white font-semibold text-sm">Bugün</span>
          <span className="text-white/60 text-sm">{formatTodayLong()}</span>
        </div>

        {/* Summary Badge */}
        {summary && summary.status !== 'not_assessed' && (
          <div className={`rounded-xl p-3 border ${summary.status === 'critical' ? 'bg-red-500/10 border-red-500/30' : summary.status === 'attention' ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/20'}`} data-testid="cardio-summary-badge">
            <p className={`text-xs font-medium ${summary.status === 'critical' ? 'text-red-300' : summary.status === 'attention' ? 'text-amber-300' : 'text-emerald-300'}`}>
              {summary.label}
            </p>
            {summary.red_flags?.length > 0 && (
              <div className="mt-2 space-y-1">
                {summary.red_flags.map((f, i) => (
                  <p key={i} className="text-red-300 text-[10px] flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {f.message}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-xl p-1">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition ${activeTab === tab.id ? 'bg-rose-500/20 text-rose-300' : 'text-white/40 hover:text-white/60'}`}
                data-testid={`cardio-tab-${tab.id}`}>
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'bp' && <BPTab onSaved={loadSummary} />}
        {activeTab === 'rhythm' && <RhythmTab onSaved={loadSummary} />}
        {activeTab === 'chest' && <ChestPainTab onSaved={loadSummary} />}
        {activeTab === 'hf' && <HFTab onSaved={loadSummary} />}

        {/* Disclaimer */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
          <p className="text-amber-200 text-[10px] text-center">
            Bu takip sistemi tıbbi tanı veya tedavi yerine geçmez. Acil durumlarda 112'yi arayın.
          </p>
        </div>

        {/* Phase 2 Placeholder */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 opacity-50" data-testid="phase2-placeholder">
          <p className="text-white/40 text-xs font-medium mb-1">Yakinda Eklenecek</p>
          <div className="space-y-1">
            <p className="text-white/30 text-[10px]">KCCQ-12 (Kansas City Kardiomiyopati Anketi)</p>
            <p className="text-white/30 text-[10px]">SAQ-7 (Seattle Angina Anketi)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardioTrackingPage;
